import SystemDashboardWidget from '@/models/SystemDashboardWidget'
import Api from '@/models/Api'
import { startCase } from 'lodash'
import moment from 'moment'
import { FILTERS } from '../../helpers'
import WeekOrMonthFilter from '../../filters/WeekOrMonthFilter'
import BaseFilter from '../../filters/BaseFilter'

const dynamicHeader = (field: any, widget: SystemDashboardWidget): string => {
  const period = startCase((widget.internalFilters.period || 'this_week').replace('_', ' '))

  if (field.key === 'label') {
    return `Won ${period}`
  }
  if (field.key === 'current_qtr') {
    // Q1, 2025
    return `Q${moment().format('Q, YYYY')}`
  }
  if (field.key === 'next_qtr') {
    return `Q${moment().add(1, 'quarter').format('Q, YYYY')}`
  }
  if (field.key === 'two_qtr_from_now') {
    return `Q${moment().add(2, 'quarter').format('Q, YYYY')}`
  }
  if (field.key === 'three_qtr_from_now') {
    return `Q${moment().add(3, 'quarter').format('Q, YYYY')}`
  }

  return field.label
}
export default class AdditionalRevenueWonQTR extends SystemDashboardWidget {
  public title: string = 'Additional Revenue Won by QTR'

  public key = 'additional_revenue_won_qtr'

  public view: string = 'table'

  constructor(args: any = null) {
    super()

    if (args && args.dimensions) {
      this.dimensions = args.dimensions
    }

    if (args && args.metrics) {
      this.metrics = args.metrics
    }

    if (args && args.filters) {
      this.filters = Object.assign(this.filters, args.filters)
    }
    if (args && args.internalFilters) {
      this.internalFilters = Object.assign(this.internalFilters, args.internalFilters)
    }

    if (args && args.rightCol) {
      this.rightCol = args.rightCol
    }

    if (args && args.leftCol) {
      this.leftCol = args.leftCol
    }

    if (args && args.size) {
      this.size = args.size
    }

    if (args && args.title) {
      this.title = args.title
    }

    if (args && args.key) {
      this.key = args.key
    }

    if (args && args.noPagination) {
      this.noPagination = args.noPagination
    }

    if (args && args.updateFields) {
      this.fields = args.updateFields(this.fields)
    }

    if (args && args.internalQuery) {
      this.internalQuery = args.internalQuery
    }
    if (args && args.internalQueryPush) {
      this.internalQuery.push(args.internalQueryPush)
    }

    if (args && args.fields) {
      this.fields = Object.assign(this.fields, args.fields)
    }
  }

  public noTotal: boolean = true

  public size: any = {
    sm: 12,
    md: 12,
    lg: 12,
  }

  // API Filters
  public filters: any = {
    region: 'all',
    product: 'all',
  }

  public internalFilters: any = {
    ...FILTERS.YEAR,
    period: 'this_week',
    view_type: 'all',
  }

  public rightCol: any = [new WeekOrMonthFilter()]

  public leftCol: any = [
    new BaseFilter({
      target: 'internalFilters',
      key: 'view_type',
      type: 'radio',
      options: [
        { text: 'All', value: 'all' },
        { text: 'Amount Change', value: 'amount_change' },
        { text: 'Closed', value: 'closed' },
      ],
    }),
  ]

  public flattenPayload = true

  public noPagination = true

  public fields = [
    {
      key: 'label',
      label: 'Won',
      class: 'text-center align-middle text-capitalize font-weight-bold',
      thClass: 'align-middle',
      thStyle: 'width: 15em',
      sortable: false,
      show: true,
      dynamicHeader,
    },
    {
      key: 'current_qtr',
      label: 'Current QTR',
      class: 'text-center align-middle',
      thClass: 'align-middle text-center',
      type: 'currency',
      sortable: false,
      show: true,
      dynamicHeader,
    },
    {
      key: 'next_qtr',
      label: 'Next QTR',
      class: 'text-center align-middle',
      thClass: 'align-middle text-center',
      type: 'currency',
      sortable: false,
      show: true,
      dynamicHeader,
    },
    {
      key: 'two_qtr_from_now',
      label: '2 QTRs Ahead',
      class: 'text-center align-middle',
      thClass: 'align-middle text-center',
      type: 'currency',
      sortable: false,
      show: true,
      dynamicHeader,
    },
    {
      key: 'three_qtr_from_now',
      label: '3 QTRs Ahead',
      class: 'text-center align-middle',
      thClass: 'align-middle text-center',
      type: 'currency',
      sortable: false,
      show: true,
      dynamicHeader,
    },
    {
      key: 'total',
      label: 'Total',
      class: 'text-center align-middle',
      thClass: 'align-middle text-center',
      type: 'currency',
      sortable: false,
      show: true,
    },
    {
      key: 'variance',
      label: 'Variance',
      class: 'text-center align-middle',
      thClass: 'align-middle text-center',
      type: 'dynamic_string',
      sortable: false,
      show: true,
    },
  ]

  protected get payload() {
    const query = [...new Set([...this.query, ...this.internalQuery])]

    const filters = {
      ...this.filters,
      ...this.internalFilters,
    }

    if (this.flattenPayload) {
      return {
        query,
        metrics: this.metrics,
        dimensions: this.dimensions,
        ...filters,
      }
    }

    return {
      metrics: this.metrics,
      dimensions: this.dimensions,
      query,
      filters,
    }
  }

  public dataSource(): any {
    let api = new Api()

    return api
      .get('slt-qtr', this.payload)
      .then((response: any) => {
        this.fields = this.fields.map(field => {
          if (field.dynamicHeader) {
            field.label = field.dynamicHeader(field, this)
          }

          return field
        })

        return response.data.result
      })
      .catch(err => ({ data: [] }))
  }
}
